/* STEP: QC Setup (Reverse only, OPTIONAL) — Clickpost "Quality Check Configuration" (A5).
   Standard QC = selectable table of the 21 predefined QC_PARAMS (enum-keyed, header
   select-all with indeterminate, live "Selected: N", info bullets, Clear, Save gated ≥1).
   Reason-based QC = CSV upload (Download Sample CSV / View Sample Parameters modal,
   three bullets, More Instructions; Save gated on upload). Step is skippable. */
import React, {useRef, useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import CheckIcon from '@mui/icons-material/Check'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import {useToast, Modal, Badge, download, cx} from '../../components'
import {QC_PARAMS, QC_REASON_CSV} from '../../dummyData'
import {Footer} from './fieldKit'

const MODES = [
  ['standard', 'Standard Quality Check', 'A selectable set of predefined QC parameters — these are sent in the Clickpost order-creation payload when reverse shipments are created.'],
  ['reason', 'Reason-based Quality Check', 'Upload a CSV mapping each return reason to YES / NO for every QC parameter.'],
]

/* View Sample Parameters modal — the reason-based template header rendered as a table. */
function SampleParamsModal({open, onClose}) {
  const lines = QC_REASON_CSV.split('\n')
  const hdr = lines[0].split(',')
  const row = (lines[1] || '').split(',')
  return (
    <Modal open={open} onClose={onClose} title='Sample Parameters' icon={<TableChartOutlinedIcon fontSize='small' />} width='min(1000px, 95vw)' actions={[{label: 'Close', primary: true, onClick: onClose}]}>
      <p className='muted' style={{fontSize: 12.5, margin: '0 0 10px'}}>
        The reason-based template header — <span className='mono'>clickpost_payload_field</span> and <span className='mono'>value</span>, then one column per QC parameter. Fill <b>YES</b>/<b>NO</b> per reason row.
      </p>
      <div style={{overflow: 'auto', maxHeight: '60vh', border: '1px solid var(--border)', borderRadius: 8}}>
        <table className='qc-tbl' style={{borderCollapse: 'collapse'}}>
          <thead>
            <tr>
              {hdr.map((h, i) => (
                <th key={i} style={{whiteSpace: 'nowrap'}}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {hdr.map((h, i) => (
                <td key={i} style={{whiteSpace: 'nowrap'}}>
                  <span className='mono'>{row[i] || ''}</span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </Modal>
  )
}
SampleParamsModal.propTypes = {open: PropTypes.bool, onClose: PropTypes.func}

export default function StepQc({W, dispatch, goBack, goNext}) {
  const toast = useToast()
  const Q = W.QC
  const mode = Q.mode || 'standard'
  const [drag, setDrag] = useState(false)
  const [showSample, setShowSample] = useState(false)
  const allRef = useRef(null)

  const setMode = (m) => dispatch({type: 'FIELD', slice: 'QC', key: 'mode', value: m})
  const setStd = (arr) => dispatch({type: 'FIELD', slice: 'QC', key: 'stdParams', value: arr})

  const allOn = Q.stdParams.length === QC_PARAMS.length
  const someOn = Q.stdParams.length > 0 && Q.stdParams.length < QC_PARAMS.length
  useEffect(() => {
    if (allRef.current) allRef.current.indeterminate = someOn
  }, [someOn, mode])

  const toggleAll = () => setStd(allOn ? [] : QC_PARAMS.map((pm) => pm.en))
  const toggleRow = (en) => setStd(Q.stdParams.indexOf(en) >= 0 ? Q.stdParams.filter((x) => x !== en) : [...Q.stdParams, en])

  const takeReason = (name) => {
    dispatch({type: 'PATCH', slice: 'QC', value: {reasonFile: name, reasonMatrix: {reasons: 12}}})
    toast('Parsed ' + name + ' — 12 return reasons × QC parameters (functional mock).', 'success')
  }

  const gate = mode === 'standard' ? Q.stdParams.length >= 1 : !!Q.reasonFile

  return (
    <div className='card card-pad' style={{maxWidth: 1400}}>
      <div className='row center' style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 4}}>
        <div style={{minWidth: 0}}>
          <h3 style={{margin: 0}}>
            Quality Check Configuration <span className='muted' style={{fontSize: 13, fontWeight: 400}}>(optional)</span>
          </h3>
          <p className='sub' style={{margin: '4px 0 0'}}>
            Define the courier-side reverse QC — which parameters the courier can accept (Standard) or the reason→parameter mapping (Reason-based).
          </p>
        </div>
        <button type='button' className='qc-tut' style={{marginLeft: 'auto'}} onClick={() => toast('Tutorial video — opens the Clickpost QC walkthrough (functional mock).', 'info')}>
          <PlayCircleOutlineIcon style={{width: 14, height: 14}} />
          Tutorial Video
        </button>
      </div>

      <div className='grid' style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, margin: '14px 0 16px'}}>
        {MODES.map((o) => {
          const on = mode === o[0]
          return (
            <label
              key={o[0]}
              className='checkrow optcard'
              style={{border: '1px solid ' + (on ? 'var(--primary)' : 'var(--border)'), borderRadius: 8, padding: 12, alignItems: 'flex-start', margin: 0, background: on ? 'var(--primary-l)' : undefined}}
            >
              <input type='radio' name='qcmode' value={o[0]} checked={on} onChange={() => setMode(o[0])} style={{marginTop: 3}} />
              <div>
                <div style={{fontWeight: 600, fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 5}}>
                  {o[1]}
                  <span className='tip' title={o[2]} style={{display: 'inline-flex', color: 'var(--neutral)'}}>
                    <InfoOutlinedIcon style={{width: 13, height: 13}} />
                  </span>
                </div>
                <div className='muted' style={{fontSize: 12, marginTop: 2}}>
                  {o[2]}
                </div>
              </div>
            </label>
          )
        })}
      </div>

      {mode === 'standard' ? (
        <>
          <div className='qc-info'>
            <InfoOutlinedIcon style={{width: 16, height: 16}} />
            <ul>
              <li>Selected clickpost api fields should be sent in clickpost order creation payload while creating reverse shipments.</li>
              <li>‘-’ indicates shipment QC checks independent of item details.</li>
            </ul>
          </div>
          <div className='row between center' style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, margin: '14px 0 8px'}}>
            <div className='seclabel' style={{margin: 0}}>
              Select our predefined quality check parameters
            </div>
            <Badge status='info' label={'Selected: ' + Q.stdParams.length} />
          </div>
          <div className='qc-scroll'>
            <table className='qc-tbl'>
              <thead>
                <tr>
                  <th style={{textAlign: 'center', width: 38}}>
                    <input ref={allRef} type='checkbox' checked={allOn} onChange={toggleAll} aria-label='Select all parameters' />
                  </th>
                  <th>Clickpost API Fields</th>
                  <th>Clickpost Enum Values</th>
                  <th>Question ID</th>
                </tr>
              </thead>
              <tbody>
                {QC_PARAMS.map((pm) => {
                  const on = Q.stdParams.indexOf(pm.en) >= 0
                  return (
                    <tr key={pm.en} className={cx('qc-row', on && 'on')}>
                      <td style={{textAlign: 'center'}}>
                        <input type='checkbox' checked={on} onChange={() => toggleRow(pm.en)} />
                      </td>
                      <td>
                        <span className='mono'>{pm.api}</span>
                      </td>
                      <td>
                        <b>{pm.en}</b>
                        {pm.ship ? (
                          <span className='tag tip' title='Shipment-level QC check — independent of item details' style={{marginLeft: 6}}>
                            shipment
                          </span>
                        ) : null}
                      </td>
                      <td>{pm.qid ? <code className='qc-qid'>{pm.qid}</code> : <span className='muted'>—</span>}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className='row center gap10' style={{display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap'}}>
            <button type='button' className='btn btn-sm' onClick={() => setStd([])}>
              Clear
            </button>
            <span className='muted' style={{fontSize: 12}}>
              Save is enabled once ≥ 1 parameter is selected. Optional step — you can Skip for now.
            </span>
          </div>
        </>
      ) : (
        <>
          <div className='qc-info'>
            <InfoOutlinedIcon style={{width: 16, height: 16}} />
            <ul>
              <li>Your CSV file should contain columns: Return Reasons, QC PARAMS (in each column).</li>
              <li>Each cell for each reason and QC parameter should contain the required YES or NO values.</li>
              <li>Ensure a maximum of 50 return reasons per CSV file.</li>
            </ul>
          </div>
          <div style={{marginTop: 12}}>
            {Q.reasonFile ? (
              <div className='svc-done'>
                <span className='svc-stamp'>
                  <CheckIcon style={{width: 14, height: 14}} />
                </span>
                <div style={{minWidth: 0}}>
                  <div style={{fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{Q.reasonFile}</div>
                  <div className='muted' style={{fontSize: 11.5}}>
                    {(Q.reasonMatrix ? Q.reasonMatrix.reasons : 0)} return reasons parsed · YES/NO per parameter
                  </div>
                </div>
                <button type='button' className='btn btn-sm' style={{marginLeft: 'auto', flexShrink: 0}} onClick={() => dispatch({type: 'PATCH', slice: 'QC', value: {reasonFile: '', reasonMatrix: null}})}>
                  Replace
                </button>
              </div>
            ) : (
              <label
                className={cx('qc-dz', drag && 'drag')}
                style={{display: 'block'}}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDrag(true)
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                  e.preventDefault()
                  setDrag(false)
                  const f = e.dataTransfer.files && e.dataTransfer.files[0]
                  if (f) takeReason(f.name)
                }}
              >
                <CloudUploadOutlinedIcon style={{width: 24, height: 24}} />
                <div style={{marginTop: 8, fontSize: 13.5}}>
                  <span className='qc-link' onClick={(e) => e.preventDefault()}>
                    Select File
                  </span>{' '}
                  <span className='muted'>or drag files here</span>
                </div>
                <input type='file' accept='.csv' style={{display: 'none'}} onChange={(e) => e.target.files[0] && takeReason(e.target.files[0].name)} />
              </label>
            )}
          </div>
          <div className='row gap16' style={{display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap'}}>
            <span
              className='qc-link'
              onClick={() => {
                download('qc-reason-based-template.csv', QC_REASON_CSV)
                toast('Sample downloaded: qc-reason-based-template.csv', 'success')
              }}
            >
              <DownloadOutlinedIcon style={{width: 13, height: 13}} /> Download Sample CSV File
            </span>
            <span className='qc-link' onClick={() => setShowSample(true)}>
              <VisibilityOutlinedIcon style={{width: 13, height: 13}} /> View Sample Parameters
            </span>
          </div>
          <button type='button' className='qc-more' onClick={() => dispatch({type: 'FIELD', slice: 'QC', key: 'moreOpen', value: !Q.moreOpen})}>
            More Instructions {Q.moreOpen ? <ExpandMoreIcon style={{width: 13, height: 13}} /> : <ChevronRightIcon style={{width: 13, height: 13}} />}
          </button>
          {Q.moreOpen ? (
            <div className='qc-more-body'>
              Save the file as UTF-8 CSV with no extra spaces or hidden characters — stray whitespace breaks the import. Keep the header row exactly as in the sample; column order is fixed. Each reason row uses the literal <span className='mono'>rvp_reason</span> in the first column and the reason label (e.g. <span className='mono'>reason_1</span>) in the second.
            </div>
          ) : null}
          <SampleParamsModal open={showSample} onClose={() => setShowSample(false)} />
        </>
      )}

      <Footer step={W.step} skip disabled={!gate} nextLabel='Save Configuration' onBack={goBack} onSkip={goNext} onNext={goNext} />
    </div>
  )
}

StepQc.propTypes = {
  W: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
}
